import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  Clock,
  LogOut,
} from 'lucide-react';
import { api } from '@/client/lib/api';
import { Button } from '@/client/components/ui/button';
import { EmptyState } from '@/client/components/ui/empty-state';
import { cn } from '@/client/lib/utils';

type SessionRow = {
  id: string;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: number;
  lastSeenAt: number;
  expiresAt: number;
};

type DeviceKind = 'phone' | 'tablet' | 'desktop';

function detectDevice(userAgent: string | null): DeviceKind {
  if (!userAgent) return 'desktop';
  const ua = userAgent.toLowerCase();
  if (/ipad|tablet/.test(ua)) return 'tablet';
  if (/mobile|iphone|android.*mobile/.test(ua)) return 'phone';
  return 'desktop';
}

function summariseUserAgent(userAgent: string | null): string {
  if (!userAgent) return '—';
  // Try to extract a friendly browser + os summary. Fall back to first 80 chars.
  const ua = userAgent;
  const browsers: Array<[RegExp, string]> = [
    [/Edg\//, 'Edge'],
    [/Chrome\//, 'Chrome'],
    [/Firefox\//, 'Firefox'],
    [/Safari\//, 'Safari'],
  ];
  const oses: Array<[RegExp, string]> = [
    [/Windows NT 10/, 'Windows'],
    [/Mac OS X|Macintosh/, 'macOS'],
    [/iPhone|iOS/, 'iOS'],
    [/iPad/, 'iPadOS'],
    [/Android/, 'Android'],
    [/Linux/, 'Linux'],
  ];
  const browser = browsers.find(([re]) => re.test(ua))?.[1];
  const os = oses.find(([re]) => re.test(ua))?.[1];
  if (browser && os) return `${browser} · ${os}`;
  if (browser) return browser;
  if (os) return os;
  return ua.slice(0, 80);
}

export function Sessions() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: ['me', 'sessions'],
    queryFn: () => api<SessionRow[]>('/api/v1/me/sessions'),
  });
  const endMutation = useMutation({
    mutationFn: (id: string) => api(`/api/v1/me/sessions/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me', 'sessions'] }),
  });

  const formatter = new Intl.DateTimeFormat(i18n.language === 'cs' ? 'cs-CZ' : 'sk-SK', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

  // Determine the "current" session: most recently active row.
  const sessions = list.data ?? [];
  let mostRecentId: string | null = null;
  let mostRecentLastSeen = -1;
  for (const session of sessions) {
    if (session.lastSeenAt > mostRecentLastSeen) {
      mostRecentLastSeen = session.lastSeenAt;
      mostRecentId = session.id;
    }
  }

  return (
    <div className="space-y-6 animate-fade-up">
      {/* Header */}
      <section className="dr-card-static relative overflow-hidden p-6 sm:p-8">
        <div
          className="absolute -top-20 -right-20 w-72 h-72 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(193,167,226,0.28) 0%, transparent 65%)',
            filter: 'blur(40px)',
          }}
          aria-hidden
        />
        <div className="relative flex items-start gap-4">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/15 text-primary shrink-0">
            <Activity size={22} />
          </div>
          <div>
            <p className="dr-eyebrow">
              {t('portal.sessions_eyebrow', { defaultValue: 'Bezpečnosť' })}
            </p>
            <h2 className="mt-1 font-display text-h3 text-primary-dark">
              {t('portal.sessions_title')}
            </h2>
            <p className="mt-2 font-sans text-body text-text-secondary leading-relaxed max-w-2xl">
              {t('portal.sessions_subtitle')}
            </p>
          </div>
        </div>
      </section>

      {list.isLoading && (
        <div className="space-y-3">
          {[0, 1].map((i) => (
            <div key={i} className="dr-card-static h-20 animate-shimmer" />
          ))}
        </div>
      )}

      {!list.isLoading && list.data?.length === 0 && (
        <EmptyState
          title={t('portal.sessions_empty')}
          description={t('portal.sessions_empty_body', {
            defaultValue: 'Tu sa zobrazia všetky aktívne prihlásenia.',
          })}
        />
      )}

      {!list.isLoading && sessions.length > 0 && (
        <ul className="space-y-3 stagger-children">
          {sessions.map((session) => {
            const device = detectDevice(session.userAgent);
            const DeviceIcon =
              device === 'phone' ? Smartphone : device === 'tablet' ? Tablet : Monitor;
            const isCurrent = session.id === mostRecentId;
            const summary = summariseUserAgent(session.userAgent);

            return (
              <li
                key={session.id}
                className={cn(
                  'rounded-2xl backdrop-blur-md border shadow-glass-soft p-4 sm:p-5 flex items-center justify-between gap-4 transition-all',
                  isCurrent
                    ? 'bg-gradient-to-br from-primary-50/60 to-white/70 border-primary/25'
                    : 'bg-white/55 border-glass-border hover:bg-white/70 hover:border-glass-border-strong',
                )}
              >
                <div className="flex items-start gap-4 min-w-0">
                  <span
                    className={cn(
                      'inline-flex h-11 w-11 items-center justify-center rounded-2xl shrink-0 border',
                      isCurrent
                        ? 'bg-gradient-to-br from-primary/20 to-primary/5 border-primary/20 text-primary'
                        : 'bg-white/70 border-glass-border-strong text-text-secondary',
                    )}
                  >
                    <DeviceIcon size={18} />
                  </span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-sans text-body font-medium text-text-primary truncate">
                        {summary}
                      </p>
                      {isCurrent && (
                        <span className="inline-flex items-center rounded-pill border border-success/25 bg-success/12 px-2.5 py-0.5 font-sans text-caption font-medium text-success">
                          {t('portal.sessions_current', { defaultValue: 'Toto zariadenie' })}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 font-sans text-caption text-text-muted">
                      <span className="inline-flex items-center gap-1.5">
                        <Globe size={12} />
                        {session.ipAddress ?? '—'}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Clock size={12} />
                        {formatter.format(new Date(session.lastSeenAt * 1000))}
                      </span>
                    </div>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => endMutation.mutate(session.id)}
                  disabled={endMutation.isPending}
                  className="shrink-0"
                >
                  <LogOut size={14} />
                  {t('portal.sessions_end')}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
